import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChecklistApi, FileReferenceDto } from '../openapi/sherlock';
import ErrorService from '../services/ErrorService';
import { useAppDispatch, useAppSelector } from '../slices/store';
import { showErrorToast } from '../slices/ToastNotificationSlice';
import { getDropboxFilesFromFileReferences } from '../utils/DropboxUtils';
import { getSherlockConfiguration } from '../utils/OpenapiConfigurationUtils';

/**
 * Custom hook to fetch and manage checklist item data
 * @param checklistItemId - The ID of the checklist item to fetch
 * @returns Object containing file references, files, and fetch function
 */
export const useChecklistItem = (checklistItemId: string) => {
  const dispatch = useAppDispatch();
  const authUserDetail = useAppSelector((state) => state.auth.authUserDetail);
  const dropbox = useAppSelector(
    (state) => state.dropbox.dropboxByBorrowerId[authUserDetail?.id!],
  );

  const [fileReferences, setFileReferences] = useState<FileReferenceDto[]>();
  const [isLoading, setIsLoading] = useState(false);

  const fetchChecklistItemById = useCallback(
    async (id: string) => {
      setIsLoading(true);
      try {
        const { data } = await new ChecklistApi(
          await getSherlockConfiguration(),
        ).getChecklistItemById(id);
        setFileReferences(data.fileReferences?.references || []);
      } catch (e: any) {
        dispatch(
          showErrorToast(
            'Unable to fetch the checklist item. Please try again after sometime.',
          ),
        );
        ErrorService.notify('Unable to fetch checklist item', e, {
          data: {
            checklistItemId: id,
          },
        });
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (checklistItemId) {
      fetchChecklistItemById(checklistItemId);
    }
  }, [checklistItemId, fetchChecklistItemById]);

  const files = useMemo(
    () => getDropboxFilesFromFileReferences(fileReferences || [], dropbox!),
    [fileReferences, dropbox],
  );

  return {
    fileReferences,
    setFileReferences,
    files,
    isLoading,
    refetch: fetchChecklistItemById,
  };
};
